import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  LinearProgress,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Alert,
  Badge,
  Divider,
} from '@mui/material';
import {
  Security,
  Visibility,
  Refresh,
  Warning,
  CheckCircle,
  Error,
  Info,
  Schedule,
  PictureAsPdf,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import {
  listDeepResearchReports,
  getDeepResearchReport,
  setCurrentReport,
  DeepResearchReport,
} from '../store/slices/deepResearchSlice';
import { fraudUtils, deepResearchAPI } from '../services/api';
import DeepResearchModal from './DeepResearchModal';

const DeepResearchReports: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { reports, loading, error } = useSelector((state: RootState) => state.deepResearch);
  
  const [selectedReport, setSelectedReport] = useState<DeepResearchReport | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [autoRefresh] = useState(true);

  useEffect(() => {
    dispatch(listDeepResearchReports({ limit: 50 }));
  }, [dispatch]);

  // Auto-refresh every 30 seconds if there are pending/processing reports
  useEffect(() => {
    if (!autoRefresh) return;

    const hasPendingReports = reports.some(r => ['pending', 'processing'].includes(r.status));
    
    if (hasPendingReports) {
      const interval = setInterval(() => {
        dispatch(listDeepResearchReports({ limit: 50 }));
      }, 30000); // Refresh every 30 seconds

      return () => clearInterval(interval);
    }
  }, [dispatch, reports, autoRefresh]);

  const handleRefresh = () => {
    dispatch(listDeepResearchReports({ limit: 50 }));
  };

  const handleViewReport = async (report: DeepResearchReport) => {
    // Always fetch the full report to get all details
    try {
      const fullReport = await dispatch(getDeepResearchReport(report.id)).unwrap();
      dispatch(setCurrentReport(fullReport));
      setSelectedReport(fullReport);
      setModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      // Fallback to showing what we have
      dispatch(setCurrentReport(report));
      setSelectedReport(report);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedReport(null);
    dispatch(setCurrentReport(null));
  };

  const handleExportPdf = async (reportId: string) => {
    try {
      const blob = await deepResearchAPI.exportToPdf(reportId);
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `deep-research-report-${reportId ? reportId.substring(0, 8) : 'unknown'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export PDF:', error);
      // You could add a toast notification here for better UX
    }
  };

  const getRiskColor = (riskLevel?: string) => {
    switch (riskLevel?.toUpperCase()) {
      case 'CRITICAL':
        return '#d32f2f';
      case 'HIGH':
        return '#f57c00';
      case 'MEDIUM':
        return '#fbc02d';
      case 'LOW':
        return '#4caf50';
      case 'MINIMAL':
        return '#9e9e9e';
      default:
        return '#9e9e9e';
    }
  };

  const getRiskIcon = (riskLevel?: string) => {
    switch (riskLevel?.toUpperCase()) {
      case 'CRITICAL':
        return <Error sx={{ color: '#d32f2f', fontSize: '1rem' }} />;
      case 'HIGH':
        return <Warning sx={{ color: '#f57c00', fontSize: '1rem' }} />;
      case 'MEDIUM':
        return <Info sx={{ color: '#fbc02d', fontSize: '1rem' }} />;
      case 'LOW':
        return <CheckCircle sx={{ color: '#4caf50', fontSize: '1rem' }} />;
      case 'MINIMAL':
        return <CheckCircle sx={{ color: '#9e9e9e', fontSize: '1rem' }} />;
      default:
        return <Schedule sx={{ color: '#9e9e9e', fontSize: '1rem' }} />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircle sx={{ color: '#4caf50', fontSize: '1rem' }} />;
      case 'pending':
        return <Schedule sx={{ color: '#ff9800', fontSize: '1rem' }} />;
      case 'processing':
        return <Info sx={{ color: '#2196f3', fontSize: '1rem' }} />;
      case 'error':
      case 'failed':
        return <Error sx={{ color: '#f44336', fontSize: '1rem' }} />;
      default:
        return <Info sx={{ color: '#9e9e9e', fontSize: '1rem' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'processing':
        return 'info';
      case 'error':
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'PENDING';
      case 'processing':
        return 'PROCESSING';
      case 'completed':
        return 'COMPLETED';
      case 'error':
      case 'failed':
        return 'FAILED';
      default:
        return status.toUpperCase();
    }
  };

  if (loading && reports.length === 0) {
    return (
      <Box sx={{ width: '100%', px: 2, py: 4 }}>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          Loading deep research reports...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', px: 2, py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Security sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" gutterBottom>
              Deep Research Reports
            </Typography>
            <Typography variant="body1" color="text.secondary">
              AI-powered fraud analysis reports from multiple investigative perspectives
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {reports.some(r => ['pending', 'processing'].includes(r.status)) && autoRefresh && (
            <Chip
              icon={<Refresh sx={{ animation: 'spin 2s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />}
              label="Auto-refreshing"
              color="info"
              size="small"
              variant="outlined"
            />
          )}
          <Button
            variant="outlined"
            onClick={handleRefresh}
            startIcon={<Refresh />}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {reports.length === 0 && !loading ? (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <CardContent>
            <Security sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Deep Research Reports Yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Deep research reports will appear here after you initiate fraud analysis from search results.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary Stats */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 4 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {reports.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Reports
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#f44336' }}>
                  {reports.filter(r => r && (r.risk_level === 'CRITICAL' || r.risk_level === 'HIGH')).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  High Risk Contracts
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#4caf50' }}>
                  {reports.filter(r => r && r.status === 'completed').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed Analyses
                </Typography>
              </CardContent>
            </Card>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#ff9800' }}>
                  {reports.filter(r => r && ['pending', 'processing'].includes(r.status)).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  In Progress
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Reports Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Report Details</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Risk Assessment</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Created</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.filter((report) => report && report.id).map((report) => (
                  <TableRow key={report.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Contract Analysis Report
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          <strong>Contract/Award ID:</strong> {report.contract_id || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          <strong>Vendor:</strong> {report.vendor_name || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          <strong>Amount:</strong> {fraudUtils.formatCurrency(report.award_amount || 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          <strong>Report ID:</strong> {report.id ? `${report.id.substring(0, 8)}...` : 'N/A'}
                        </Typography>
                        {report.contract_data?.additional_data?.url && (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography
                              component="a"
                              href={report.contract_data.additional_data.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="caption"
                              color="primary.main"
                              sx={{
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              <LinkIcon sx={{ fontSize: 12 }} />
                              View Contract
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {report.status === 'completed' && report.risk_level ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <Chip
                            icon={getRiskIcon(report.risk_level)}
                            label={report.risk_level}
                            sx={{
                              bgcolor: getRiskColor(report.risk_level) + '20',
                              color: getRiskColor(report.risk_level),
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                            size="small"
                          />
                          {report.fraud_risk_score !== undefined && (
                            <Typography variant="body2" sx={{ fontWeight: 600, color: getRiskColor(report.risk_level) }}>
                              {report.fraud_risk_score}/100
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          Analysis pending
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Badge
                        color={['pending', 'processing'].includes(report.status) ? 'warning' : 'default'}
                        variant="dot"
                        invisible={!['pending', 'processing'].includes(report.status)}
                      >
                        <Chip
                          icon={getStatusIcon(report.status)}
                          label={getStatusLabel(report.status)}
                          color={getStatusColor(report.status) as any}
                          variant="outlined"
                          size="small"
                        />
                      </Badge>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {new Date(report.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="View Report Details">
                          <IconButton
                            onClick={() => handleViewReport(report)}
                            color="primary"
                            size="small"
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        {report.status === 'completed' && (
                          <Tooltip title="Download PDF">
                            <IconButton
                              onClick={() => handleExportPdf(report.id)}
                              color="primary"
                              size="small"
                            >
                              <PictureAsPdf />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Deep Research Modal */}
      <DeepResearchModal
        open={modalOpen}
        onClose={handleCloseModal}
        contract={selectedReport ? {
          id: selectedReport.contract_id,
          title: selectedReport.contract_data?.contract_description || 'Contract Analysis Report',
          vendor: selectedReport.vendor_name,
          vendor_name: selectedReport.vendor_name,
          amount: selectedReport.award_amount,
          total_obligated_amount: selectedReport.award_amount,
          agency: selectedReport.contract_data?.awarding_agency || 'Unknown Agency',
          date: selectedReport.created_at,
          fraud_risk_level: selectedReport.risk_level,
          url: selectedReport.contract_data?.additional_data?.url,
          usaspending_permalink: selectedReport.contract_data?.additional_data?.usaspending_permalink,
          award_details: {
            award_id: selectedReport.contract_id,
            start_date: selectedReport.contract_data?.award_date,
            contracting_agency: selectedReport.contract_data?.awarding_agency,
          },
        } as any : null}
      />
    </Box>
  );
};

export default DeepResearchReports;